/**
 * friendlyApiError — Convert whatever a failed API call rejects with into
 * a message safe to show in a user-facing toast.
 *
 * postApiCall's own .catch usually rejects with a string (the backend's
 * `data.message`), so this normally passes that through untouched. But
 * some endpoints leak Sequelize / raw SQL / stack-trace text into the
 * message field, and those are noise to the end user — we swap those
 * for a generic "Something went wrong. Please try again." so we never
 * expose DB internals in a toast.
 */
const DB_ERROR_MARKERS = [
  'Sequelize',           // ORM name
  'SequelizeError',
  'ER_',                 // MySQL error code prefix — ER_DUP_ENTRY, ER_NO_SUCH_TABLE, …
  'ETIMEDOUT',
  'ECONNREFUSED',
  'SQLITE_',
  'SQL syntax',
  'sql syntax',
  'ORA-',                // Oracle error prefix
  'PG::',                // Postgres
  'duplicate key',
  'foreign key constraint',
  'violates',
  'ENOENT',
  'TypeError:',
  'ReferenceError:',
  'Cannot read propert',
  'Cannot set propert',
  'undefined is not',
  'is not defined',
  'at Object.',          // stack-trace frame
  'at async ',
];

const GENERIC = 'Something went wrong. Please try again.';

export function friendlyApiError(err: unknown): string {
  if (Array.isArray(err)) {
    const joined = err.filter(Boolean).map(String).join('. ');
    return friendlyApiError(joined);
  }
  const raw =
    typeof err === 'string'
      ? err
      : Array.isArray((err as any)?.explanation)
        ? (err as any).explanation.filter(Boolean).join('. ')
        : (err as any)?.message || '';
  if (!raw) return GENERIC;

  // Anything that looks like a DB / stack-trace leak → swap for generic.
  if (DB_ERROR_MARKERS.some((m) => raw.includes(m))) return GENERIC;

  // Absurdly long messages are almost always a stack trace or a raw JSON blob.
  if (raw.length > 240) return GENERIC;

  return raw;
}
